import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Wallet, DollarSign, PiggyBank, Shield, Archive } from "lucide-react";
import { API_URL } from "../../components/config";
const BalanceCards = () => {
  const [balances, setBalances] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        console.log("Fetching balance data...");
        const response = await fetch(
          `${API_URL}/topUp/get_all_entity_total_balances`
        );
        const result = await response.json();
        console.log("Balance API response:", result);
        
        if (result.success && result.data) {
          console.log("Setting balances:", result.data);
          // Ensure we always have valid data structure
          if (Array.isArray(result.data) && result.data.length > 0) {
            // Flatten and ensure all wallet types are present
            const allBalances = [];
            const walletTypes = [
              'user_wallet_total_balance',
              'main_wallet_total_balance', 
              'revenue_wallet_total_balance',
              'vendor_wallet_total_balance',
              'escrow_total_balance'
            ];
            
            // Extract all balances from the API response
            const extractedBalances = {};
            result.data.forEach(group => {
              if (Array.isArray(group) && group.length > 0) {
                group.forEach(balance => {
                  const key = Object.keys(balance)[0];
                  if (key) {
                    extractedBalances[key] = balance[key];
                  }
                });
              }
            });
            
            console.log("Extracted balances:", extractedBalances);
            
            // Create cards for all wallet types, using 0 for missing ones
            walletTypes.forEach(walletType => {
              allBalances.push([{ [walletType]: extractedBalances[walletType] || 0 }]);
            });
            
            console.log("Final balances array:", allBalances);
            setBalances(allBalances);
          } else {
            console.log("Invalid data structure, using fallback");
            setBalances([
              [
                { user_wallet_total_balance: 0 },
                { main_wallet_total_balance: 0 },
                { revenue_wallet_total_balance: 0 },
                { vendor_wallet_total_balance: 0 },
                { escrow_total_balance: 0 }
              ]
            ]);
          }
        } else {
          console.log("API response not successful, using fallback data");
          // Fallback data if API doesn't work - ensure cards always show
          setBalances([
            [
              { user_wallet_total_balance: 0 },
              { main_wallet_total_balance: 0 },
              { revenue_wallet_total_balance: 0 },
              { vendor_wallet_total_balance: 0 },
              { escrow_total_balance: 0 }
            ]
          ]);
        }
      } catch (error) {
        console.error("Error fetching balance data:", error);
        console.log("Using fallback data due to error");
        // Fallback data on error - ensure cards always show
        setBalances([
          [
            { user_wallet_total_balance: 0 },
            { main_wallet_total_balance: 0 },
            { revenue_wallet_total_balance: 0 },
            { vendor_wallet_total_balance: 0 },
            { escrow_total_balance: 0 }
          ]
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const getBalanceIcon = (type) => {
    switch (type) {
      case "user_wallet_total_balance":
        return <Wallet className="w-6 h-6 text-blue-500" />;
      case "main_wallet_total_balance":
        return <PiggyBank className="w-6 h-6 text-green-500" />;
      case "main_wallet_balance":
        return <DollarSign className="w-6 h-6 text-purple-500" />;
      case "revenue_wallet_total_balance":
        return <PiggyBank className="w-6 h-6 text-yellow-500" />;
      case "vendor_wallet_total_balance":
        return <Archive className="w-6 h-6 text-red-500" />;
      case "escrow_total_balance":
        return <Shield className="w-6 h-6 text-indigo-500" />;
      default:
        return <DollarSign className="w-6 h-6 text-gray-500" />;
    }
  };

  const getBalanceLabel = (type) => {
    return type
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const getBalanceColor = (type) => {
    switch (type) {
      case "user_wallet_total_balance":
        return "from-blue-500 to-blue-600";
      case "main_wallet_total_balance":
        return "from-green-500 to-green-600";
      case "main_wallet_balance":
        return "from-purple-500 to-purple-600";
      case "revenue_wallet_total_balance":
        return "from-yellow-500 to-yellow-600";
      case "vendor_wallet_total_balance":
        return "from-red-500 to-red-600";
      case "escrow_total_balance":
        return "from-indigo-500 to-indigo-600";
      default:
        return "from-gray-500 to-gray-600";
    }
  };

  return (
    <div className="p-6 overflow-x-auto">
      {loading ? (
        <div className="flex gap-6">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="w-72 flex-shrink-0">
              <div className="bg-white border-2 border-gray-200 rounded-xl shadow-lg overflow-hidden">
                <div className="bg-gray-300 p-4 animate-pulse">
                  <div className="flex items-center justify-between">
                    <div className="w-6 h-6 bg-gray-400 rounded"></div>
                    <div className="w-24 h-4 bg-gray-400 rounded"></div>
                  </div>
                </div>
                <div className="p-6">
                  <div className="h-4 bg-gray-300 rounded mb-2 w-20"></div>
                  <div className="h-8 bg-gray-300 rounded w-32"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-nowrap gap-6 min-w-full">
          {console.log("Rendering balances:", balances)}
          {balances.length > 0 ? (
            balances.flatMap((balanceGroup, groupIndex) => {
              console.log("Processing balanceGroup:", balanceGroup);
              return balanceGroup.map((balance, index) => {
                const balanceType = Object.keys(balance)[0];
                const balanceValue = balance[balanceType];
                
                console.log("Card data:", { balanceType, balanceValue });

                return (
                  <motion.div
                    key={`${groupIndex}-${index}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    className="w-72 flex-shrink-0"
                  >
                    <div className="bg-white border-2 border-[#68B3F0] rounded-xl shadow-lg overflow-hidden">
                      <div
                        className={`bg-gradient-to-r ${getBalanceColor(
                          balanceType
                        )} p-4`}
                      >
                        <div className="flex items-center justify-between">
                          {getBalanceIcon(balanceType)}
                          <span className="text-white text-sm font-medium">
                            {getBalanceLabel(balanceType)}
                          </span>
                        </div>
                      </div>
                      <div className="p-6">
                        <h3 className="text-sm font-medium text-gray-500 mb-2">
                          Balance
                        </h3>
                        <div className="flex items-end gap-2">
                          <span className="text-2xl font-bold text-gray-700">
                            ${Number(balanceValue || 0).toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              });
            })
          ) : (
            <div className="text-gray-500 text-center py-8">
              No balance data available
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BalanceCards;
